import { useRef, useState, useMemo, useEffect, ReactNode } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useDocumentState } from '@/state'
import { useListTemplateAttributes } from '@/services/queries/template_attributes'

export const AnnotationsMultiSelectContainer = ({
  children,
  isReadOnly,
}: {
  children: ReactNode
  isReadOnly?: boolean
}) => {
  const currentPageInView = useDocumentState((state) => state.currentPageInView)
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const { data: templateAttributes } = useListTemplateAttributes({
    documentPageId: currentPageInView,
  })
  const croppingImageId = useDocumentState((state) => state.croppingImageId)

  const containerRef = useRef<HTMLDivElement>(null)
  const [selectionBox, setSelectionBox] = useState({
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
  })
  const getDraggableAnnotationParentRef = useDocumentState(
    useShallow((state) => state.getDraggableAnnotationParentRef),
  )
  const zoom = useDocumentState((state) => state.zoom)
  const zoomPercentage = useMemo(() => zoom / 100, [zoom])
  const [isSelecting, setIsSelecting] = useState(false)

  const setDocumentState = useDocumentState(
    useShallow((state) => state.setState),
  )

  const setMultiSelectDelta = useDocumentState(
    useShallow((state) => state.setMultiSelectDelta),
  )

  const startX = selectionBox.startX / zoomPercentage
  const endX = selectionBox.endX / zoomPercentage
  const startY = selectionBox.startY / zoomPercentage
  const endY = selectionBox.endY / zoomPercentage

  const selectionStyle = {
    left: Math.min(startX, endX),
    top: Math.min(startY, endY),
    width: Math.abs(endX - startX),
    height: Math.abs(endY - startY),
  }

  useEffect(() => {
    if (
      isReadOnly ||
      !Array.isArray(templateAttributes) ||
      !isSelecting ||
      !containerRef.current
    ) {
      return
    }

    // Selection box normalized (already divided by zoomPercentage above)
    const selLeft = Math.min(startX, endX)
    const selTop = Math.min(startY, endY)
    const selRight = Math.max(startX, endX)
    const selBottom = Math.max(startY, endY)

    const containerRect = containerRef.current.getBoundingClientRect()

    const inBoundTemplateAttributes = templateAttributes.filter((tmpl) => {
      if (!tmpl.id) return false

      const parentEl = getDraggableAnnotationParentRef(tmpl.id)
      if (!parentEl) return false

      const templateEl = parentEl.lastChild as HTMLDivElement
      if (!templateEl) return false

      const tmplRect = templateEl.getBoundingClientRect()

      // Convert to container-relative coords, then normalize by zoom
      const tmplLeft = (tmplRect.left - containerRect.left) / zoomPercentage
      const tmplTop = (tmplRect.top - containerRect.top) / zoomPercentage
      const tmplRight = tmplLeft + tmplRect.width / zoomPercentage
      const tmplBottom = tmplTop + tmplRect.height / zoomPercentage

      // Rectangle overlap test
      const overlaps = !(
        tmplLeft > selRight ||
        tmplRight < selLeft ||
        tmplTop > selBottom ||
        tmplBottom < selTop
      )

      return overlaps
    })

    setDocumentState({
      bulkSelectedTemplateAttributes: inBoundTemplateAttributes,
    })
  }, [
    endX,
    endY,
    getDraggableAnnotationParentRef,
    isSelecting,
    selectionBox,
    setDocumentState,
    startX,
    startY,
    templateAttributes,
    zoomPercentage,
    isReadOnly,
  ])

  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelHoldTimer = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current)
      holdTimerRef.current = null
    }
  }

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current)
        holdTimerRef.current = null
      }
    }
  }, [])

  if (isReadOnly) {
    return children
  }

  return (
    <div
      data-testid="annotations-multi-select-container"
      className="w-full h-full"
      ref={containerRef}
      onMouseDown={(e) => {
        if (isReadOnly || !containerRef.current || !!croppingImageId) return
        if (selectedElement?.id) {
          const parentEl = getDraggableAnnotationParentRef(selectedElement.id)
          if (parentEl && parentEl.contains(e.target as Node)) {
            return
          }
        }

        setDocumentState({
          bulkSelectedTemplateAttributes: [],
        })

        const rect = containerRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top

        setSelectionBox({ startX: x, startY: y, endX: x, endY: y })
        setMultiSelectDelta({ x: 0, y: 0 })

        const HOLD_MS = 200

        cancelHoldTimer()
        holdTimerRef.current = setTimeout(() => {
          setIsSelecting(true)
        }, HOLD_MS)
      }}
      onMouseUp={() => {
        if (isReadOnly) return
        cancelHoldTimer()
        setIsSelecting(false)
      }}
      onMouseMove={(e) => {
        if (isReadOnly || !containerRef.current || !isSelecting) return

        const rect = containerRef.current.getBoundingClientRect()

        const x = e.clientX - rect.x
        const y = e.clientY - rect.y

        setSelectionBox((prev) => ({ ...prev, endX: x, endY: y }))
      }}
    >
      {children}
      {!isReadOnly && isSelecting && (
        <div
          className="absolute border-2 border-blue-500 bg-blue-100 bg-opacity-20 pointer-events-none z-[100]"
          style={{
            left: selectionStyle.left,
            top: selectionStyle.top,
            width: selectionStyle.width,
            height: selectionStyle.height,
          }}
        />
      )}
    </div>
  )
}
